import React, { useEffect, useState } from 'react';
import {
  Box,
  type BoxProps,
  Skeleton,
  type SkeletonProps,
  Stack,
  Typography,
} from '@mui/material';

import { useReducedMotion } from '@/hooks/useReducedMotion';

import {
  STUDENT_LONG_WAIT_MS,
  useStudentLoadingSlot,
} from './StudentContentBoundary';

export const StudentSkeleton: React.FC<SkeletonProps> = props => {
  const reducedMotion = useReducedMotion();
  return (
    <Skeleton
      {...props}
      animation={reducedMotion ? false : (props.animation ?? 'wave')}
      aria-hidden="true"
    />
  );
};

export interface StudentLoadingRegionProps extends Omit<BoxProps, 'children'> {
  label: string;
  children: React.ReactNode;
  /** Tests may shorten this; product surfaces keep the eight-second contract. */
  longWaitMs?: number;
}

/**
 * Accessible owner for a skeleton composition. Decorative placeholders stay
 * hidden; the region emits exactly one polite announcement and a delayed
 * human-readable reassurance for genuinely slow requests.
 */
export const StudentLoadingRegion: React.FC<StudentLoadingRegionProps> = ({
  label,
  children,
  longWaitMs = STUDENT_LONG_WAIT_MS,
  sx,
  ...boxProps
}) => {
  const parentSlot = useStudentLoadingSlot();
  const [takingLonger, setTakingLonger] = useState(false);

  useEffect(() => {
    if (parentSlot.nested) return undefined;
    const timeout = window.setTimeout(() => setTakingLonger(true), longWaitMs);
    return () => window.clearTimeout(timeout);
  }, [longWaitMs, parentSlot.nested]);

  const showLongWait = parentSlot.nested
    ? parentSlot.takingLonger
    : takingLonger;

  return (
    <Box
      {...boxProps}
      role={parentSlot.nested ? undefined : 'status'}
      aria-live={parentSlot.nested ? undefined : 'polite'}
      aria-busy={parentSlot.nested ? undefined : 'true'}
      aria-label={parentSlot.nested ? undefined : label}
      sx={sx}
    >
      {children}
      {showLongWait ? (
        <Typography
          sx={{
            mt: 'var(--sp-md)',
            color: 'var(--ink-muted)',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          This is taking longer than usual. Check your connection or reload the
          page.
        </Typography>
      ) : null}
    </Box>
  );
};

export const StudentUpdatingStatus: React.FC<{
  active: boolean;
  label?: string;
}> = ({ active, label = 'Updating…' }) => {
  if (!active) return null;

  // Routine revalidation must not create a visible row or a permanent blank
  // gutter. The existing content remains the visual feedback; this live
  // region supplies the equivalent state change for assistive technology.
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        p: 0,
        m: '-1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {label}
    </Box>
  );
};

const CardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    aria-hidden="true"
    sx={{
      p: { xs: '18px', sm: '22px' },
      borderRadius: 'var(--radius-xl)',
      border: '1px solid var(--border)',
      backgroundColor: 'var(--paper)',
      boxShadow: 'var(--shadow-soft)',
    }}
  >
    {children}
  </Box>
);

export const StudentHeroSkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading your practice',
}) => (
  <StudentLoadingRegion label={label}>
    <CardShell>
      <StudentSkeleton variant="rounded" width={142} height={20} />
      <StudentSkeleton
        variant="text"
        width="72%"
        height={34}
        sx={{ mt: '10px' }}
      />
      <StudentSkeleton
        variant="rounded"
        width="100%"
        height={54}
        sx={{ mt: '14px', borderRadius: 'var(--radius-md)' }}
      />
      <StudentSkeleton
        variant="rounded"
        width="100%"
        height={52}
        sx={{ mt: '12px', borderRadius: 'var(--radius-pill)' }}
      />
    </CardShell>
  </StudentLoadingRegion>
);

export const StudentTopicListSkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading topics',
}) => (
  <StudentLoadingRegion label={label}>
    <StudentSkeleton
      variant="text"
      width={136}
      height={28}
      sx={{ mb: '8px' }}
    />
    <Stack spacing="10px" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <StudentSkeleton
          key={index}
          variant="rounded"
          height={76}
          sx={{ borderRadius: 'var(--radius-lg)' }}
        />
      ))}
    </Stack>
  </StudentLoadingRegion>
);

export const StudentQuestionSkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading your question',
}) => (
  <StudentLoadingRegion label={label}>
    <CardShell>
      <StudentSkeleton variant="text" width="82%" height={30} />
      <StudentSkeleton variant="text" width="64%" height={30} />
      <Stack spacing="8px" sx={{ mt: '18px' }} aria-hidden="true">
        {Array.from({ length: 4 }).map((_, index) => (
          <StudentSkeleton
            key={index}
            variant="rounded"
            height={54}
            sx={{ borderRadius: 'var(--radius-md)' }}
          />
        ))}
      </Stack>
      <StudentSkeleton
        variant="rounded"
        height={52}
        sx={{ mt: '16px', borderRadius: 'var(--radius-pill)' }}
      />
    </CardShell>
  </StudentLoadingRegion>
);

export const StudentMapSkeleton: React.FC<{
  label?: string;
  insights?: boolean;
}> = ({ label = 'Loading your progress', insights = false }) => {
  if (insights) {
    return (
      <StudentLoadingRegion label={label}>
        <StudentSkeleton
          variant="rounded"
          height={116}
          sx={{ mt: '20px', borderRadius: 'var(--radius-lg)' }}
        />
        <Box
          aria-hidden="true"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: { xs: '8px', sm: '10px' },
            mt: '20px',
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <StudentSkeleton
              key={index}
              variant="rounded"
              height={78}
              sx={{ borderRadius: 'var(--radius-lg)' }}
            />
          ))}
        </Box>
        <StudentSkeleton
          variant="rounded"
          height={320}
          sx={{ mt: '24px', borderRadius: 'var(--radius-xl)' }}
        />
      </StudentLoadingRegion>
    );
  }

  return (
    <StudentLoadingRegion label={label}>
      <Box
        aria-hidden="true"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          gap: { xs: '8px', sm: '10px' },
          mt: '20px',
        }}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <StudentSkeleton
            key={index}
            variant="rounded"
            height={78}
            sx={{ borderRadius: 'var(--radius-lg)' }}
          />
        ))}
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: '12px',
          mt: '24px',
        }}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <StudentSkeleton
            key={index}
            variant="rounded"
            height={126}
            sx={{ borderRadius: 'var(--radius-lg)' }}
          />
        ))}
      </Box>
      <StudentSkeleton
        variant="rounded"
        height={430}
        sx={{ mt: '24px', borderRadius: 'var(--radius-xl)' }}
      />
    </StudentLoadingRegion>
  );
};

export const StudentRecapSkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading your session recap',
}) => (
  <StudentLoadingRegion label={label}>
    <CardShell>
      <StudentSkeleton variant="text" width={130} height={20} />
      <StudentSkeleton
        variant="text"
        width="62%"
        height={44}
        sx={{ mt: '6px' }}
      />
      <StudentSkeleton variant="rounded" height={82} sx={{ mt: '16px' }} />
    </CardShell>
    <Stack spacing="10px" sx={{ mt: '16px' }} aria-hidden="true">
      {Array.from({ length: 3 }).map((_, index) => (
        <StudentSkeleton
          key={index}
          variant="rounded"
          height={82}
          sx={{ borderRadius: 'var(--radius-md)' }}
        />
      ))}
    </Stack>
  </StudentLoadingRegion>
);

export const StudentHistorySkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading your history',
}) => (
  <StudentLoadingRegion label={label}>
    <StudentSkeleton variant="text" width={140} height={24} />
    <Stack spacing="10px" sx={{ mt: '10px' }} aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <StudentSkeleton
          key={index}
          variant="rounded"
          height={64}
          sx={{ borderRadius: 'var(--radius-md)' }}
        />
      ))}
    </Stack>
  </StudentLoadingRegion>
);

export const StudentRequestsSkeleton: React.FC<{ label?: string }> = ({
  label = 'Loading your requests',
}) => (
  <StudentLoadingRegion label={label}>
    <Stack spacing="12px" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, index) => (
        <StudentSkeleton
          key={index}
          variant="rounded"
          height={104}
          sx={{ borderRadius: 'var(--radius-lg)' }}
        />
      ))}
    </Stack>
  </StudentLoadingRegion>
);

export type StudentRouteSkeletonVariant =
  | 'tasks'
  | 'practice'
  | 'progress-map'
  | 'progress-insights'
  | 'topic'
  | 'question'
  | 'recap'
  | 'session-review'
  | 'history'
  | 'requests'
  | 'diagnostic';

export const studentRouteLoadingLabel = (
  variant: StudentRouteSkeletonVariant
): string => {
  switch (variant) {
    case 'tasks':
      return 'Loading your tasks';
    case 'practice':
      return 'Loading practice';
    case 'progress-map':
    case 'progress-insights':
      return 'Loading your progress';
    case 'topic':
      return 'Loading topic map';
    case 'question':
    case 'diagnostic':
      return 'Loading your question';
    case 'recap':
      return 'Loading your session recap';
    case 'session-review':
      return 'Loading session review';
    case 'history':
      return 'Loading your history';
    case 'requests':
      return 'Loading your requests';
  }
};

export const StudentRouteSkeleton: React.FC<{
  variant: StudentRouteSkeletonVariant;
}> = ({ variant }) => {
  const mapSurface = variant === 'progress-map' || variant === 'topic';
  const maxWidth =
    mapSurface || variant === 'history' || variant === 'session-review'
      ? 960
      : variant === 'requests'
        ? { xs: '100%', sm: 800, md: 1200, lg: 1400 }
        : 680;

  let body: React.ReactNode;
  switch (variant) {
    case 'progress-map':
    case 'topic':
      body = <StudentMapSkeleton />;
      break;
    case 'progress-insights':
      body = <StudentMapSkeleton insights />;
      break;
    case 'question':
    case 'diagnostic':
      body = <StudentQuestionSkeleton />;
      break;
    case 'recap':
    case 'session-review':
      body = <StudentRecapSkeleton />;
      break;
    case 'history':
      body = <StudentHistorySkeleton />;
      break;
    case 'requests':
      body = <StudentRequestsSkeleton />;
      break;
    case 'tasks':
      body = <StudentHeroSkeleton label="Loading your tasks" />;
      break;
    case 'practice':
    default:
      body = <StudentTopicListSkeleton label="Loading practice" />;
      break;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <StudentSkeleton
        aria-hidden="true"
        variant="text"
        width={variant === 'tasks' ? 230 : 180}
        height={42}
        sx={{ mb: '18px' }}
      />
      {body}
    </Box>
  );
};

export function studentRouteSkeletonVariant(
  pathname: string,
  search = ''
): StudentRouteSkeletonVariant | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/tasks') return 'tasks';
  if (path === '/practice') return 'practice';
  if (/^\/practice\/topic\//.test(path)) return 'topic';
  if (path === '/practice/session') return 'question';
  if (path === '/practice/session/ended') return 'recap';
  if (path === '/progress') {
    return new URLSearchParams(search).get('view') === 'insights'
      ? 'progress-insights'
      : 'progress-map';
  }
  if (path === '/history') return 'history';
  if (/^\/history\/homework\//.test(path)) return 'question';
  if (/^\/history\/session\//.test(path)) return 'session-review';
  if (path === '/my-requests') return 'requests';
  if (path === '/diagnostic') return 'diagnostic';
  if (/^\/homework\//.test(path)) return 'question';
  return null;
}
